/**
 * BillDetectiveDigestToggle — opt-in widget for the Monday Slack digest
 * (Engine #29 multiplier). Lives on the Bill Detective page.
 *
 * Flow:
 *   1. Loads current setting via GET /api/bill-detective/digest/settings
 *   2. Toggle flips enabled, optional per-org webhook URL
 *   3. "Preview" button shows the exact Slack blocks that will be posted
 *   4. "Send now" button fires a one-off (auditable)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Slack, Eye, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function BillDetectiveDigestToggle() {
  const [enabled, setEnabled] = useState(false);
  const [webhook, setWebhook] = useState('');
  const [lastSent, setLastSent] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/bill-detective/digest/settings`, auth())
      .then((r) => {
        setEnabled(!!r.data.enabled);
        setWebhook(r.data.webhook_url || '');
        setLastSent(r.data.last_sent_at);
      })
      .catch(() => {});
  }, []);

  const save = async (nextEnabled) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/bill-detective/digest/settings`,
        { enabled: nextEnabled, webhook_url: webhook || null },
        auth());
      setEnabled(r.data.settings.enabled);
      toast.success(r.data.settings.enabled
        ? 'Monday digest enabled — first send next Monday 09:00 UTC'
        : 'Monday digest disabled');
    } catch (e) {
      toast.error('Could not save digest setting');
    } finally {
      setBusy(false);
    }
  };

  const doPreview = async () => {
    try {
      const r = await axios.get(`${API}/bill-detective/digest/preview`, auth());
      setPreview(r.data);
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const doSendNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bill-detective/digest/send-now`, {}, auth());
      if (r.data.sent) {
        toast.success(`Digest sent · delivery: ${r.data.delivery}`);
        setLastSent(new Date().toISOString());
      } else {
        toast.message(`Skipped: ${r.data.reason}`);
      }
    } catch (e) {
      toast.error('Send failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className="bg-slate-900/40 ring-1 ring-slate-800 mt-8"
      data-testid="bd-digest-toggle"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2">
              <Slack className="w-4 h-4 text-emerald-300" />
              <h3 className="text-sm font-medium text-slate-100">
                Slack weekly digest — Mondays 09:00 UTC
              </h3>
            </div>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">
              Auto-posts the top saver + top spender of the week to your Slack workspace.
              Engineers see their name in #engineering → cost becomes a team-culture moment, not a CFO spreadsheet.
            </p>
            {lastSent && (
              <p className="text-[11px] text-slate-500 mt-1">
                <CheckCircle2 className="w-3 h-3 inline text-emerald-400 mr-1" />
                Last sent: {new Date(lastSent).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs ${enabled ? 'text-emerald-300' : 'text-slate-500'}`}>
              {enabled ? 'On' : 'Off'}
            </span>
            <Switch
              checked={enabled}
              onCheckedChange={(v) => save(v)}
              disabled={busy}
              data-testid="bd-digest-switch"
            />
          </div>
        </div>

        {enabled && (
          <div className="mt-4 space-y-3" data-testid="bd-digest-config">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">
                Slack incoming-webhook URL <span className="text-slate-500">(optional — defaults to org-wide channel)</span>
              </label>
              <Input
                value={webhook}
                onChange={(e) => setWebhook(e.target.value)}
                onBlur={() => save(enabled)}
                placeholder="https://hooks.slack.com/services/T../B../..."
                className="bg-slate-950 border-slate-700 text-slate-100 text-xs font-mono"
                data-testid="bd-digest-webhook-input"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={doPreview}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="bd-digest-preview-btn"
              >
                <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview blocks
              </Button>
              <Button
                size="sm"
                onClick={doSendNow}
                disabled={busy}
                className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950"
                data-testid="bd-digest-send-now-btn"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" /> Send a test digest now
              </Button>
            </div>

            {preview && (
              <div className="mt-3 p-3 rounded-md bg-slate-950 ring-1 ring-slate-800" data-testid="bd-digest-preview">
                <div className="text-[10px] text-slate-500 mb-1 uppercase tracking-wide">Preview</div>
                <pre className="text-xs text-slate-300 whitespace-pre-wrap">
{preview.blocks
  .map((b) => b.text?.text || (b.elements && b.elements[0]?.text) || '')
  .filter(Boolean)
  .join('\n')}
                </pre>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
